import { Component, OnInit, Input, output, ViewChild } from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import { DatePipe } from '@angular/common';
import { ISearchMaskOccupancy, IRoomOccupancy, ISmCategory, ISearchParams, IEndpoint, isValidStayLength } from 'core-lib';
import { OutSideClickDirective } from './outside-click.directive';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SettingsService } from 'core-lib';
import { SelectModule } from 'primeng/select';
import { AvailabilityService } from 'core-lib';
import { BreakpointObserverService } from 'core-lib';
import { StorageService } from 'core-lib';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'core-lib';
import { PIMService } from 'core-lib';
import { DatepickerComponent } from './datepicker/datepicker.component';
import { RoomPickerComponent } from './room-picker/room-picker.component';
import { Router } from '@angular/router';
import dayjs from 'dayjs';

@Component({
    selector: 'lib-searchmask',
    imports: [
        DatePipe,
        FormsModule,
        ReactiveFormsModule,
        OutSideClickDirective,
        ButtonModule,
        InputTextModule,
        SelectModule,
        TranslateModule,
        DatepickerComponent,
        RoomPickerComponent
    ],
    templateUrl: './searchmask-lib.component.html',
    styleUrl: './searchmask-lib.component.scss'
})
export class SearchmaskComponent implements OnInit{

  @Input() showSearchMask = true;
  @Input() isCustomerSearchmask = false; //if the searchmask component is in the app or in customer's website

  @ViewChild(DatepickerComponent, { static: false })
  private datePickerComponent: DatepickerComponent;

  valueChanged = output<boolean>() //for detecting if any relevant searchmask values have changed.
  
  chainCode: string;
  bookingCode: FormControl = new FormControl('');
  stay:Date[] = [];
  occupancy:ISearchMaskOccupancy; 
  roomData: IRoomOccupancy[];
  searchParams:ISearchParams|null = null;
  roomPickerShown:boolean = false;
  categories:ISmCategory[] = [];
  hotels: {hotelCode:string, hotelName:string}[];
  isMobile = false;
  language:string;
  roomPickerTouched:boolean = false;


  categorySelect: FormControl = new FormControl(null);
  hotelSelect: FormControl = new FormControl(null);

  constructor(
    public settings: SettingsService, 
    private avService: AvailabilityService, 
    private breakpoints: BreakpointObserverService, 
    private storage: StorageService,
    private languageService: LanguageService,
    private pimService: PIMService,
    private translate: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {

    this.chainCode =this.settings?.get('app.chainCode') || '';
    this.language = this.languageService.getCurrentLanguage();

    let smFromStorage:ISearchParams|null = this.storage.loadSessionStorage('searchparams');

    let hasInvalidStay = false;
    if (smFromStorage?.arrival && smFromStorage.departure) {
      const arrival = new Date(smFromStorage.arrival);
      if (isValidStayLength(smFromStorage.arrival, smFromStorage.departure)) {
        this.stay = [arrival, new Date(smFromStorage.departure)];
      }
      else {
        // e.g. deeplink with a zero, negative or too long stay: default to one night and let the user pick the dates
        this.stay = [arrival, dayjs(arrival).add(1, 'day').toDate()];
        hasInvalidStay = true;
      }
    }
    if (smFromStorage?.bookingCode) {
      this.bookingCode.setValue(smFromStorage.bookingCode);
    }

    let bp = this.breakpoints.screenSizeObserver.subscribe((val) => {
      if (val < 768) {
        this.isMobile = true
      }
      else {
        this.isMobile = false
        this.showSearchMask =true;

      }
    });
    
    this.initHotels();
    // init ategories if only one hotel
    if (this.settings?.get('app.endpoints')?.length == 1) {
      this.initCategories(); 
    }

    
    if (smFromStorage && smFromStorage.roomData) {
      this.roomData = smFromStorage.roomData;
      this.updateOccupancy(smFromStorage.roomData, false);

      // e.g. deeplink without child ages: open the room picker, which shows the missing ages.
      // With an invalid stay the datepicker opens first and opens the room picker after the dates are picked.
      if (!hasInvalidStay && smFromStorage.roomData.some(room => room.childAges?.some(age => age === null))) {
        // needs to be inside timeout because outsideclick directive would close it instantly
        setTimeout(() => this.showRoomPicker(), 200);
      }
    }
    else {
      this.updateOccupancy(
        [{
          adults: this.settings?.get('searchMask.defaultAdults'),
          children: this.settings?.get('searchMask.defaultChildren'),
          childAges: []
        }],
        false
      );
    }

    if (hasInvalidStay) {
      // needs to be inside timeout because the datepicker is not rendered yet
      setTimeout(() => this.datePickerComponent?.open(), 200);
    }

    this.bookingCode.valueChanges.subscribe((value:string) => {
      this.valueChanged.emit(true);
    });

    this.hotelSelect.valueChanges.subscribe((value:string) => {
      this.valueChanged.emit(true);
      setTimeout(() => {
        this.datePickerComponent.ngOnInit();
      },100);
    })
    
  }

  search():void {

    const hasUnselectedAges = this.roomData?.some(room => room.childAges.some(age => age === null));
    if (hasUnselectedAges) {
      setTimeout(() => this.showRoomPicker());
      return;
    }

    //set always zero hours for dates

    this.stay[0].setHours(0);
    this.stay[0].setMinutes(0);
    this.stay[0].setSeconds(0);
    this.stay[0].setMilliseconds(0);
  
    this.stay[1].setHours(0);
    this.stay[1].setMinutes(0);
    this.stay[1].setSeconds(0);
    this.stay[1].setMilliseconds(0);

    this.searchParams = {
      bookingCode: this.bookingCode.value,
      arrival: this.stay[0],
      departure: this.stay[1],
      roomData: this.roomData,
      hotelCode: this.hotelSelect.value,
      chainCode: this.chainCode
    };

    if (this.hotels?.length == 1) {
      this.searchParams.hotelCode = this.hotels[0].hotelCode;
    }
    if (this.categories?.length > 0) {
      this.searchParams.category = this.categorySelect.value;
    }

    if (this.isCustomerSearchmask) {
      this.avService.makeDeeplinkSearch(this.searchParams);
    }
    else {
      this.avService.makeSearch(this.searchParams);
      this.valueChanged.emit(false);
    }

  }

  datePickerChanged(val:Date[]):void {
    this.stay = val;
    this.valueChanged.emit(true);

    let isAvailability = this.router?.url?.includes('availability');

    if (!isAvailability) {
      if (val[1] != null && !this.roomPickerTouched) {
        // needs to  be inside timeout because outsideclick directive would close it instantly
        setTimeout(() => {
          this.showRoomPicker();
        }, 200);
      }
    }
    
  }

  toggleRoomPicker($event?:MouseEvent) {
    if ($event) {
      $event.stopPropagation();
    }
    if (this.roomPickerShown) {
      this.hideRoomPicker();
    }
    else {
      this.showRoomPicker();
    }
  }
  showRoomPicker() {
    // set touched, so its not always automatically opened
    this.roomPickerTouched = true;

    this.roomPickerShown = true;
    
    if (this.isMobile) {
      document.querySelector('body')?.classList.add('modal-is-open');
    }
  }

  hideRoomPicker() {    
    this.roomPickerShown = false;
    if (this.isMobile) {
      document.querySelector('body')?.classList.remove('modal-is-open');
    }
  }

  openSearchMask() {
    this.showSearchMask = true;
  }

  updateOccupancy(roomData:IRoomOccupancy[], callValueChanges: boolean) {
    this.roomData = roomData;
    let rooms = roomData.length;
    let adults = 0;
    let children = 0;
    roomData.forEach(room => {
      adults += room.adults;
      children += room.children;
    });

    this.occupancy = {
      rooms: rooms,
      adults: adults,
      children: children
    };
    if (callValueChanges) {
      this.valueChanged.emit(true);
    }
  }

  initCategories() {
    let smFromStorage:ISearchParams|null = this.storage.loadSessionStorage('searchparams');

    let categoriesInSettings = this.settings?.getCategories(this.language);
    if (categoriesInSettings && categoriesInSettings.length > 0) {
 
      let hasAll = categoriesInSettings.find((item:ISmCategory) => {return item.value == 'ALL'});
      // add all category in the beginning
      if (!hasAll) {
        categoriesInSettings.unshift({value: 'ALL', name: this.translate.instant('sm.categoryAll')})
      }
      this.categories = categoriesInSettings;
      if (smFromStorage) {
        this.categorySelect.setValue(smFromStorage.category);
      }
      else {
        this.categorySelect.setValue('ALL');  
      }
    }
  }

  initHotels() {
    let smFromStorage:ISearchParams|null = this.storage.loadSessionStorage('searchparams');

    let endpoints = this.settings?.get('app.endpoints');

    if (endpoints?.length > 1) {
      this.hotels = endpoints.map((ep:IEndpoint) => {
        return {
          hotelCode: ep.hotelCode,
          hotelName: this.pimService.getHotelInfo(ep.hotelCode)?.name
        }
      });

      this.hotels.unshift({
        hotelCode: 'ALL',
        hotelName: this.translate.instant('sm.allHotels')
      })
  
      if (smFromStorage) {
        this.hotelSelect.setValue(smFromStorage.hotelCode);
      }
    }
    else {
      //if only one endpoint, set hotelSelect to be that, 
      //this.settings.getEndpoint();
      this.hotelSelect.setValue(endpoints[0].hotelCode);
    }

    console.log('selected hotel', this.hotelSelect.value);
    
  }

  getHotelName(hotelCode:string):string {
    if (this.hotels) {
      let hotel = this.hotels.find(h => h.hotelCode == hotelCode);
      if (hotel) {
        return hotel.hotelName;
      }
    }
    return '';
  }
}

